import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1503455637927-730bce8583c0?auto=format&fit=crop&w=2400&q=80";

// ============================================================
// LANDING — /
// ============================================================
const Landing = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [revealTitle, setRevealTitle] = useState(false);

  const [poOpen, setPoOpen] = useState(false);
  const [poName, setPoName] = useState("");
  const [poEmail, setPoEmail] = useState("");
  const [poSubmitting, setPoSubmitting] = useState(false);
  const [poDone, setPoDone] = useState(false);
  const poInputRef = useRef(null);

  useEffect(() => {
    const t1 = setTimeout(() => setLoaded(true), 80);
    const t2 = setTimeout(() => setRevealTitle(true), 3200);
    try {
      const stored = JSON.parse(localStorage.getItem("mn_identity") || "null");
      if (stored?.email) {
        setEmail(stored.email);
        setPoEmail(stored.email);
      }
      if (stored?.name) {
        setName(stored.name);
        setPoName(stored.name);
      }
      if (stored?.submitted) setSubmitted(true);
    } catch (_) {}
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    if (poOpen) {
      document.body.style.overflow = "hidden";
      setTimeout(() => poInputRef.current?.focus(), 200);
    } else {
      document.body.style.overflow = "";
    }
    const onKey = (e) => {
      if (e.key === "Escape" && poOpen) setPoOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [poOpen]);

  const openPreorder = () => {
    setPoDone(false);
    setPoOpen(true);
  };

  const submitPreorder = async (e) => {
    e.preventDefault();
    const cleanEmail = poEmail.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setPoSubmitting(true);
    try {
      await axios.post(`${API}/preorder/interest`, {
        name: poName.trim() || null,
        email: cleanEmail,
      });
      setPoDone(true);
      try {
        localStorage.setItem(
          "mn_identity",
          JSON.stringify({ email: cleanEmail, name: poName.trim(), submitted: true })
        );
      } catch (_) {}
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setPoSubmitting(false);
    }
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/notify`, {
        name: name.trim() || null,
        email: cleanEmail,
        source: "landing",
      });
      setSubmitted(true);
      try {
        localStorage.setItem(
          "mn_identity",
          JSON.stringify({ email: cleanEmail, name: name.trim(), submitted: true })
        );
      } catch (_) {}
      toast.success("You are on the list.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="landing-page"
      className={`mn-page ${loaded ? "is-loaded" : ""}`}
    >
      <div
        className="mn-hero-image"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
        data-testid="hero-image"
      />
      <div className="mn-vignette" aria-hidden="true" />
      <div className="mn-tone" aria-hidden="true" />
      <div className="mn-grain" aria-hidden="true" />

      <nav className="mn-nav" data-testid="top-nav">
        <span className="mn-mark">MN</span>
        <Link to="/links" className="mn-domain mn-nav-link" data-testid="nav-links">
          maritaniska.no
        </Link>
      </nav>

      <section className="mn-stage">
        <div className="mn-stage-inner">
          <p className="mn-eyebrow reveal" data-testid="eyebrow">
            <span className="mn-line-left" />
            <span>A new book is coming</span>
            <span className="mn-line-right" />
          </p>

          <h1 className="mn-name reveal reveal-delay-1" data-testid="author-name">
            MARITA NISKA
          </h1>

          <h2
            className={`mn-title ${revealTitle ? "is-revealed" : ""}`}
            data-testid="book-title"
          >
            <span className="mn-title-main">NARCISSIST</span>
            <span className="mn-title-sub">— The Beauty and the Beast —</span>
            <span className="mn-title-foot">Four years in London</span>
          </h2>

          <p className="mn-sub reveal reveal-delay-2" data-testid="limited-text">
            Limited first edition pre-order.
          </p>

          <div className="mn-cta reveal reveal-delay-3">
            <button
              type="button"
              onClick={openPreorder}
              className="mn-btn"
              data-testid="preorder-btn"
              aria-label="Pre-order the limited first edition"
            >
              <span className="mn-btn-label">PRE&nbsp;-&nbsp;ORDER</span>
              <span className="mn-btn-arrow" aria-hidden="true">→</span>
            </button>

            <form
              onSubmit={handleNotify}
              className={`mn-notify ${submitted ? "is-done" : ""}`}
              data-testid="notify-form"
              noValidate
            >
              {!submitted ? (
                <div className="mn-notify-fields">
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mn-input mn-input-name"
                    aria-label="Your name"
                    data-testid="notify-name-input"
                    disabled={submitting}
                  />
                  <div className="mn-notify-row">
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="Your email — be notified first"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mn-input mn-input-email"
                      aria-label="Email address"
                      data-testid="notify-email-input"
                      disabled={submitting}
                      required
                    />
                    <button
                      type="submit"
                      className="mn-submit"
                      disabled={submitting}
                      data-testid="notify-submit-btn"
                      aria-label="Notify me"
                    >
                      {submitting ? "···" : "NOTIFY ME"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mn-done" data-testid="notify-done">
                  You are on the list. Silence will be broken soon.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      <footer className="mn-foot" data-testid="footer">
        <span className="mn-foot-left">MMXXVI</span>
        <span className="mn-foot-mid">Wild. Brutal. Scars. True story.</span>
        <Link to="/links" className="mn-foot-right mn-foot-link" data-testid="footer-links">
          OSLO · LONDON
        </Link>
      </footer>

      {poOpen && (
        <div
          className="mn-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="po-title"
          data-testid="preorder-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !poSubmitting) setPoOpen(false);
          }}
        >
          <div className="mn-overlay-inner">
            <button
              type="button"
              className="mn-close"
              onClick={() => setPoOpen(false)}
              aria-label="Close"
              data-testid="preorder-close"
              disabled={poSubmitting}
            >
              ✕
            </button>

            {!poDone ? (
              <>
                <p className="mn-eyebrow mn-overlay-eyebrow">
                  <span className="mn-line-left" />
                  <span>Reserve your copy</span>
                  <span className="mn-line-right" />
                </p>
                <h3 id="po-title" className="mn-overlay-title">
                  Limited first edition
                </h3>
                <p className="mn-overlay-lede">
                  Leave your name and we will reach out personally when the
                  first edition opens.
                </p>

                <form className="mn-overlay-form" onSubmit={submitPreorder} noValidate>
                  <input
                    ref={poInputRef}
                    type="text"
                    autoComplete="name"
                    placeholder="Name"
                    value={poName}
                    onChange={(e) => setPoName(e.target.value)}
                    className="mn-input mn-input-name"
                    aria-label="Your name"
                    data-testid="preorder-name-input"
                    disabled={poSubmitting}
                  />
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Email"
                    value={poEmail}
                    onChange={(e) => setPoEmail(e.target.value)}
                    className="mn-input mn-input-name"
                    aria-label="Email address"
                    data-testid="preorder-email-input"
                    disabled={poSubmitting}
                    required
                  />
                  <button
                    type="submit"
                    className="mn-btn mn-overlay-submit"
                    disabled={poSubmitting}
                    data-testid="preorder-submit-btn"
                  >
                    <span className="mn-btn-label">
                      {poSubmitting ? "RESERVING…" : "RESERVE"}
                    </span>
                    <span className="mn-btn-arrow" aria-hidden="true">→</span>
                  </button>
                </form>
              </>
            ) : (
              <div className="mn-overlay-done" data-testid="preorder-done">
                <p className="mn-eyebrow mn-overlay-eyebrow">
                  <span className="mn-line-left" />
                  <span>Reserved</span>
                  <span className="mn-line-right" />
                </p>
                <h3 className="mn-overlay-title">Your place is held.</h3>
                <p className="mn-overlay-lede">
                  You are now part of the first edition.<br />
                  We will be in touch personally.
                </p>
                <p className="mn-overlay-signature">— Marita Niska</p>
                <button
                  type="button"
                  className="mn-overlay-close-btn"
                  onClick={() => setPoOpen(false)}
                  data-testid="preorder-done-close"
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            color: "#e8e6e1",
            border: "1px solid #1a1a1a",
            borderRadius: 0,
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "0.95rem",
            letterSpacing: "0.04em",
          },
        }}
      />
    </main>
  );
};

// ============================================================
// LINKS — /links  (cinematic "link in bio")
// ============================================================
const LINK_ITEMS = [
  {
    label: "Pre-order the book",
    sub: "Narcissist · First edition",
    kind: "internal-preorder",
    testid: "link-preorder",
  },
  {
    label: "Instagram",
    sub: "@maritaniska",
    href: "https://instagram.com/maritaniska",
    external: true,
    testid: "link-instagram",
  },
  {
    label: "Journal",
    sub: "Fragments & dispatches",
    kind: "internal-journal",
    testid: "link-journal",
  },
  {
    label: "Mailing list",
    sub: "Notified before the world",
    kind: "internal-mailing",
    testid: "link-mailing",
  },
];

const Links = () => {
  const [loaded, setLoaded] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [mlOpen, setMlOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    try {
      const s = JSON.parse(localStorage.getItem("mn_identity") || "null");
      if (s?.email) setFormEmail(s.email);
      if (s?.name) setFormName(s.name);
    } catch (_) {}
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const open = poOpen || mlOpen;
    if (open) {
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 200);
      setDone(false);
    } else {
      document.body.style.overflow = "";
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPoOpen(false);
        setMlOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [poOpen, mlOpen]);

  const handleItemClick = (item, e) => {
    if (item.kind === "internal-preorder") {
      e.preventDefault();
      setPoOpen(true);
    } else if (item.kind === "internal-mailing") {
      e.preventDefault();
      setMlOpen(true);
    } else if (item.kind === "internal-journal") {
      e.preventDefault();
      navigate("/journal");
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const cleanEmail = formEmail.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const endpoint = poOpen ? "/preorder/interest" : "/notify";
      const payload = poOpen
        ? { name: formName.trim() || null, email: cleanEmail }
        : { name: formName.trim() || null, email: cleanEmail, source: "links" };
      await axios.post(`${API}${endpoint}`, payload);
      setDone(true);
      try {
        localStorage.setItem(
          "mn_identity",
          JSON.stringify({ email: cleanEmail, name: formName.trim(), submitted: true })
        );
      } catch (_) {}
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const overlayOpen = poOpen || mlOpen;
  const closeOverlay = () => {
    setPoOpen(false);
    setMlOpen(false);
  };

  const overlayCopy = poOpen
    ? {
        eyebrow: "Reserve your copy",
        title: "Limited first edition",
        lede: "Leave your name and we will reach out personally when the first edition opens.",
        cta: "RESERVE",
        doneTitle: "Your place is held.",
        doneLede: "You are now part of the first edition.\nWe will be in touch personally.",
      }
    : {
        eyebrow: "Mailing list",
        title: "Be notified first",
        lede: "Receive quiet dispatches before the world knows.",
        cta: "JOIN",
        doneTitle: "You are on the list.",
        doneLede: "Silence will be broken soon.",
      };

  return (
    <main
      data-testid="links-page"
      className={`mn-links-page ${loaded ? "is-loaded" : ""}`}
    >
      <div className="mn-grain" aria-hidden="true" />
      <div className="mn-links-vignette" aria-hidden="true" />

      <nav className="mn-nav" data-testid="links-nav">
        <Link to="/" className="mn-mark mn-nav-link" data-testid="links-home">
          MN
        </Link>
        <span className="mn-domain">maritaniska.no/links</span>
      </nav>

      <section className="mn-links-stage">
        <header className="mn-links-header">
          <p className="mn-eyebrow reveal" data-testid="links-eyebrow">
            <span className="mn-line-left" />
            <span>The directory</span>
            <span className="mn-line-right" />
          </p>
          <h1 className="mn-links-author reveal reveal-delay-1" data-testid="links-author">
            MARITA NISKA
          </h1>
          <p className="mn-links-sub reveal reveal-delay-2">
            <em>NARCISSIST — Wild. Brutal. Scars. True story.</em>
          </p>
        </header>

        <ol className="mn-links-list" data-testid="links-list">
          {LINK_ITEMS.map((item, idx) => {
            const isInternal = item.kind?.startsWith("internal-");
            const Tag = isInternal ? "button" : "a";
            const props = isInternal
              ? { type: "button", onClick: (e) => handleItemClick(item, e) }
              : {
                  href: item.href,
                  target: item.external ? "_blank" : undefined,
                  rel: item.external ? "noopener noreferrer" : undefined,
                };
            return (
              <li key={item.label} className="mn-links-item" style={{ "--i": idx }}>
                <Tag
                  {...props}
                  className="mn-links-row"
                  data-testid={item.testid}
                  aria-label={item.label}
                >
                  <span className="mn-links-num">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="mn-links-text">
                    <span className="mn-links-label">{item.label}</span>
                    <span className="mn-links-sublabel">{item.sub}</span>
                  </span>
                  <span className="mn-links-arrow" aria-hidden="true">
                    →
                  </span>
                  <span className="mn-links-rule" aria-hidden="true" />
                </Tag>
              </li>
            );
          })}
        </ol>
      </section>

      <footer className="mn-foot" data-testid="links-footer">
        <Link to="/" className="mn-foot-left mn-foot-link" data-testid="links-back">
          ← MARITANISKA.NO
        </Link>
        <span className="mn-foot-mid">MMXXVI</span>
        <span className="mn-foot-right">OSLO · LONDON</span>
      </footer>

      {overlayOpen && (
        <div
          className="mn-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="links-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) closeOverlay();
          }}
        >
          <div className="mn-overlay-inner">
            <button
              type="button"
              className="mn-close"
              onClick={closeOverlay}
              aria-label="Close"
              data-testid="links-overlay-close"
              disabled={busy}
            >
              ✕
            </button>

            {!done ? (
              <>
                <p className="mn-eyebrow mn-overlay-eyebrow">
                  <span className="mn-line-left" />
                  <span>{overlayCopy.eyebrow}</span>
                  <span className="mn-line-right" />
                </p>
                <h3 className="mn-overlay-title">{overlayCopy.title}</h3>
                <p className="mn-overlay-lede">{overlayCopy.lede}</p>

                <form className="mn-overlay-form" onSubmit={submitForm} noValidate>
                  <input
                    ref={inputRef}
                    type="text"
                    autoComplete="name"
                    placeholder="Name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="mn-input mn-input-name"
                    aria-label="Your name"
                    data-testid="links-name-input"
                    disabled={busy}
                  />
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    className="mn-input mn-input-name"
                    aria-label="Email address"
                    data-testid="links-email-input"
                    disabled={busy}
                    required
                  />
                  <button
                    type="submit"
                    className="mn-btn mn-overlay-submit"
                    disabled={busy}
                    data-testid="links-submit-btn"
                  >
                    <span className="mn-btn-label">
                      {busy ? "···" : overlayCopy.cta}
                    </span>
                    <span className="mn-btn-arrow" aria-hidden="true">→</span>
                  </button>
                </form>
              </>
            ) : (
              <div className="mn-overlay-done" data-testid="links-overlay-done">
                <p className="mn-eyebrow mn-overlay-eyebrow">
                  <span className="mn-line-left" />
                  <span>Confirmed</span>
                  <span className="mn-line-right" />
                </p>
                <h3 className="mn-overlay-title">{overlayCopy.doneTitle}</h3>
                <p className="mn-overlay-lede" style={{ whiteSpace: "pre-line" }}>
                  {overlayCopy.doneLede}
                </p>
                <p className="mn-overlay-signature">— Marita Niska</p>
                <button
                  type="button"
                  className="mn-overlay-close-btn"
                  onClick={closeOverlay}
                  data-testid="links-overlay-done-close"
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            color: "#e8e6e1",
            border: "1px solid #1a1a1a",
            borderRadius: 0,
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "0.95rem",
            letterSpacing: "0.04em",
          },
        }}
      />
    </main>
  );
};

// ============================================================
// JOURNAL — /journal  (placeholder, cinematic "in silence")
// ============================================================
const Journal = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      data-testid="journal-page"
      className={`mn-links-page mn-journal-page ${loaded ? "is-loaded" : ""}`}
    >
      <div className="mn-grain" aria-hidden="true" />
      <div className="mn-links-vignette" aria-hidden="true" />

      <nav className="mn-nav" data-testid="journal-nav">
        <Link to="/" className="mn-mark mn-nav-link" data-testid="journal-home">
          MN
        </Link>
        <Link
          to="/links"
          className="mn-domain mn-nav-link"
          data-testid="journal-to-links"
        >
          maritaniska.no/links
        </Link>
      </nav>

      <section className="mn-journal-stage">
        <p className="mn-eyebrow reveal" data-testid="journal-eyebrow">
          <span className="mn-line-left" />
          <span>Journal</span>
          <span className="mn-line-right" />
        </p>
        <h1
          className="mn-journal-title reveal reveal-delay-1"
          data-testid="journal-title"
        >
          In silence,<br />
          <em>still writing</em>.
        </h1>
        <p
          className="mn-journal-lede reveal reveal-delay-2"
          data-testid="journal-lede"
        >
          Fragments and dispatches will appear here soon —<br />
          quiet entries from the world of <em>Narcissist</em>.
        </p>
        <Link
          to="/links"
          className="mn-overlay-close-btn mn-journal-back reveal reveal-delay-3"
          data-testid="journal-back"
        >
          RETURN
        </Link>
      </section>

      <footer className="mn-foot" data-testid="journal-footer">
        <Link to="/" className="mn-foot-left mn-foot-link">
          ← MARITANISKA.NO
        </Link>
        <span className="mn-foot-mid">MMXXVI</span>
        <span className="mn-foot-right">OSLO · LONDON</span>
      </footer>
    </main>
  );
};



// ============================================================
// ADMIN — /admin (private dashboard)
// ============================================================
const ADMIN_TOKEN_KEY = "mn_admin_token";

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return err?.message || "Something went wrong.";
};

const adminAxios = axios.create();
adminAxios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const Admin = () => {
  const [authed, setAuthed] = useState(null); // null=checking, false=login, true=in
  const [loaded, setLoaded] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [tab, setTab] = useState("subscribers");
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      if (!token) {
        setAuthed(false);
        return;
      }
      try {
        await adminAxios.get(`${API}/auth/me`);
        setAuthed(true);
      } catch (_) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthed(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (authed !== true) return;
    setLoadingData(true);
    Promise.all([
      adminAxios.get(`${API}/admin/stats`).then((r) => r.data),
      adminAxios.get(`${API}/notify`).then((r) => r.data),
      adminAxios.get(`${API}/preorder/events`).then((r) => r.data),
    ])
      .then(([s, sb, po]) => {
        setStats(s);
        setSubs(sb);
        setPreorders(po);
      })
      .catch((e) => {
        console.error(e);
        if (e?.response?.status === 401) {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          setAuthed(false);
        } else {
          toast.error(formatErr(e));
        }
      })
      .finally(() => setLoadingData(false));
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: loginEmail.trim().toLowerCase(),
        password: loginPassword,
      });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setLoginPassword("");
      setAuthed(true);
    } catch (err) {
      setLoginErr(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminAxios.post(`${API}/auth/logout`);
    } catch (_) {}
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
    setStats(null);
    setSubs([]);
    setPreorders([]);
  };

  const downloadCsv = async (which) => {
    try {
      const resp = await adminAxios.get(`${API}/admin/export/${which}.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${which}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (authed === null) {
    return (
      <main className="mn-links-page is-loaded">
        <div className="mn-grain" aria-hidden="true" />
        <div className="mn-admin-stage mn-admin-loading" data-testid="admin-loading">
          <p className="mn-eyebrow"><span className="mn-line-left" /><span>One moment</span><span className="mn-line-right" /></p>
        </div>
      </main>
    );
  }

  if (authed === false) {
    return (
      <main
        data-testid="admin-login-page"
        className={`mn-links-page ${loaded ? "is-loaded" : ""}`}
      >
        <div className="mn-grain" aria-hidden="true" />
        <div className="mn-links-vignette" aria-hidden="true" />

        <nav className="mn-nav">
          <Link to="/" className="mn-mark mn-nav-link">MN</Link>
          <span className="mn-domain">maritaniska.no/admin</span>
        </nav>

        <section className="mn-admin-stage">
          <div className="mn-admin-login-card">
            <p className="mn-eyebrow reveal" data-testid="admin-eyebrow">
              <span className="mn-line-left" />
              <span>Private quarters</span>
              <span className="mn-line-right" />
            </p>
            <h1 className="mn-admin-title reveal reveal-delay-1">ADMIN</h1>
            <p className="mn-admin-sub reveal reveal-delay-2">
              <em>For Marita only.</em>
            </p>

            <form className="mn-admin-form reveal reveal-delay-3" onSubmit={handleLogin}>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="mn-input mn-input-name"
                data-testid="admin-email-input"
                required
                disabled={busy}
              />
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="mn-input mn-input-name"
                data-testid="admin-password-input"
                required
                disabled={busy}
              />
              {loginErr && (
                <p className="mn-admin-error" data-testid="admin-error">{loginErr}</p>
              )}
              <button
                type="submit"
                className="mn-btn mn-overlay-submit"
                disabled={busy}
                data-testid="admin-login-btn"
              >
                <span className="mn-btn-label">{busy ? "…" : "ENTER"}</span>
                <span className="mn-btn-arrow" aria-hidden="true">→</span>
              </button>
            </form>
          </div>
        </section>

        <footer className="mn-foot">
          <Link to="/" className="mn-foot-left mn-foot-link">← MARITANISKA.NO</Link>
          <span className="mn-foot-mid">MMXXVI</span>
          <span className="mn-foot-right">OSLO · LONDON</span>
        </footer>

        <Toaster theme="dark" position="bottom-center" />
      </main>
    );
  }

  return (
    <main
      data-testid="admin-dashboard"
      className={`mn-links-page mn-admin-dash ${loaded ? "is-loaded" : ""}`}
    >
      <div className="mn-grain" aria-hidden="true" />

      <nav className="mn-nav">
        <Link to="/" className="mn-mark mn-nav-link">MN</Link>
        <button
          type="button"
          onClick={handleLogout}
          className="mn-domain mn-nav-link mn-logout-btn"
          data-testid="admin-logout-btn"
        >
          Sign out ✕
        </button>
      </nav>

      <section className="mn-admin-dash-stage">
        <header className="mn-admin-header">
          <p className="mn-eyebrow"><span className="mn-line-left" /><span>The list</span><span className="mn-line-right" /></p>
          <h1 className="mn-admin-title">MARITA NISKA</h1>
        </header>

        <div className="mn-admin-stats" data-testid="admin-stats">
          <div className="mn-stat">
            <span className="mn-stat-num">{stats?.subscribers_total ?? "…"}</span>
            <span className="mn-stat-label">Subscribers</span>
          </div>
          <div className="mn-stat">
            <span className="mn-stat-num">{stats?.unique_preorder_emails ?? "…"}</span>
            <span className="mn-stat-label">Pre-order interest</span>
          </div>
          <div className="mn-stat">
            <span className="mn-stat-num">{stats?.preorder_events_total ?? "…"}</span>
            <span className="mn-stat-label">Pre-order events</span>
          </div>
        </div>

        <div className="mn-admin-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`mn-tab ${tab === "subscribers" ? "is-active" : ""}`}
            onClick={() => setTab("subscribers")}
            data-testid="tab-subscribers"
          >
            Subscribers
          </button>
          <button
            type="button"
            role="tab"
            className={`mn-tab ${tab === "preorders" ? "is-active" : ""}`}
            onClick={() => setTab("preorders")}
            data-testid="tab-preorders"
          >
            Pre-order interest
          </button>
          <button
            type="button"
            className="mn-export-link"
            onClick={() => downloadCsv(tab === "subscribers" ? "subscribers" : "preorders")}
            data-testid="export-btn"
          >
            Export CSV ↓
          </button>
        </div>

        <div className="mn-table-wrap">
          {loadingData ? (
            <p className="mn-admin-empty">Loading…</p>
          ) : tab === "subscribers" ? (
            subs.length === 0 ? (
              <p className="mn-admin-empty">No subscribers yet.</p>
            ) : (
              <table className="mn-table" data-testid="subscribers-table">
                <thead>
                  <tr>
                    <th>#</th><th>Name</th><th>Email</th><th>Source</th><th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s, i) => (
                    <tr key={s.id || s.email}>
                      <td className="mn-td-num">{String(i + 1).padStart(2, "0")}</td>
                      <td>{s.name || <span className="mn-td-dim">—</span>}</td>
                      <td><a href={`mailto:${s.email}`} className="mn-td-email">{s.email}</a></td>
                      <td className="mn-td-dim">{s.source || "—"}</td>
                      <td className="mn-td-date">{fmtDate(s.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : preorders.length === 0 ? (
            <p className="mn-admin-empty">No pre-order interest yet.</p>
          ) : (
            <table className="mn-table" data-testid="preorders-table">
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Email</th><th>Status</th><th>Time</th>
                </tr>
              </thead>
              <tbody>
                {preorders.map((p, i) => (
                  <tr key={p.id || `${p.email}-${i}`}>
                    <td className="mn-td-num">{String(i + 1).padStart(2, "0")}</td>
                    <td>{p.name || <span className="mn-td-dim">—</span>}</td>
                    <td><a href={`mailto:${p.email}`} className="mn-td-email">{p.email}</a></td>
                    <td className="mn-td-dim">{p.status}</td>
                    <td className="mn-td-date">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <footer className="mn-foot">
        <Link to="/" className="mn-foot-left mn-foot-link">← MARITANISKA.NO</Link>
        <span className="mn-foot-mid">MMXXVI</span>
        <span className="mn-foot-right">OSLO · LONDON</span>
      </footer>

      <Toaster theme="dark" position="bottom-center" />
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/links" element={<Links />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
